import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PlaceEmployeeDialog from './PlaceEmployeeDialog';
import { getPlaceEmployees, deletePlaceEmployment } from '../../services/placeService';
import { EMPLOYEE_GRID_COLUMNS } from '../../constants/placeEmployeesTabConstants';
import FormSpinner from 'components/FormSpinner';

const PlaceEmployeesTab = ({ ptsPlaceID }) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  const placePerms = usePermissions(PERMISSION_RESOURCES.PLACE);
  const employeesPerms = getFullPermissions('globals', 'Employees', 'any');
  const canAddPlaceEmployee = Boolean(placePerms.Edit) && Boolean(employeesPerms.Create);
  const canEditPlaceEmployee = Boolean(placePerms.Edit) && Boolean(employeesPerms.Edit);
  const canDeletePlaceEmployee = Boolean(placePerms.Edit) && Boolean(employeesPerms.Delete);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadEmployees = useCallback(async () => {
    if (!ptsPlaceID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPlaceEmployees(ptsPlaceID),
      onSuccess: (data) => setEmployees(data),
    });
  }, [dispatch, ptsPlaceID]);

  const handleAdd = useCallback(() => {
    setEditingEmployee(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingEmployee(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingEmployee(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadEmployees();
  }, [handleFormClose, loadEmployees]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to remove "${row.FullName || 'this employee'}" from this place?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deletePlaceEmployment({ ptsPlaceID, ptsPersonID: row.ptsPersonID }),
            onSuccess: () => {
              dispatch(notify('Employee removed successfully', 'success'));
              loadEmployees();
            },
          });
        },
        'Remove Employee'
      );
    },
    [dispatch, ptsPlaceID, requestConfirm, loadEmployees]
  );

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  return (
    <Box className="person-names-tab__grid-wrap place-grid-tab">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Employee"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canAddPlaceEmployee}
        />
      </Box>

      <CommonDataGrid
        rows={employees}
        columns={EMPLOYEE_GRID_COLUMNS}
        getRowId={(row) => row.rowKey}
        toolbar={getDataGridToolbarProps({ printTitle: 'Place Employees' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPlaceEmployee },
          delete: { visible: true, onClick: handleDelete, disabled: !canDeletePlaceEmployee },
        }}
      />

      {formOpen && (
        <PlaceEmployeeDialog
          open={formOpen}
          ptsPlaceID={ptsPlaceID}
          employee={editingEmployee}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PlaceEmployeesTab;
